import { QueryClient, useMutation, useQueryClient } from "react-query";
import { loggedInApiSingleConcurrentCall } from "src/api/axiosClient/userApi";
import { getOneActionPlanKey } from "src/api/client-api/ActionPlan/ActionPlan/useGetOneActionPlan";
import { getIngredientScoresKey } from "src/api/client-api/olapScores/useGetIngredientScores";
import { getPostScoresKey } from "src/api/client-api/olapScores/useGetPostScores";
import { getProductScoresKey } from "src/api/client-api/olapScores/useGetProductScores";
import { getOneIngredientKey } from "src/api/client-api/ingredients/useGetOneIngredient";
import { getOneProductKey } from "src/api/client-api/products/useGetOneProduct";
import { getCompanyScoresHeadingsApiKey } from "src/pages/Company/Scores/CompanyScoreContextProvider";
import { useSnackbar } from "src/utils/useSnackbar";
import { useTranslation } from "react-i18next";
import { calculationFailureMessage, classifyCalculationFailure } from "./calculationFailure";
import { getOrganizationPostApiKey } from "./useGetOrganizationPost";
import { getIngredientPostKey } from "./useGetIngredientPost";
import { getIngredientPostsKey } from "./useGetIngredientPosts";
import { getOnePostKey } from "./useGetOnePost";
import { getPackagingPostKey } from "./useGetPackagingPost";
import { getPackagingsPostsKey } from "./useGetPackagingsPosts";
import { getProductPostKey } from "./useGetProductPost";

type RecalculatePostOptions = {
    postId: number;
};

export const recalculatePostFn = async (options: RecalculatePostOptions) => {
    return await loggedInApiSingleConcurrentCall.post(
        `/api/ssq-posts/${options.postId}/recalculate`,
        {},
        {
            headers: {
                Accept: "application/json",
            },
        },
    );
};

export const invalidateRecalculatedScores = (queryClient: QueryClient) => {
    queryClient.invalidateQueries([getCompanyScoresHeadingsApiKey]);
    queryClient.invalidateQueries([getOneIngredientKey]);
    queryClient.invalidateQueries([getIngredientPostKey]);
    queryClient.invalidateQueries([getIngredientPostsKey]);
    queryClient.invalidateQueries([getIngredientScoresKey]);
    queryClient.invalidateQueries([getOneProductKey]);
    queryClient.invalidateQueries([getProductPostKey]);
    queryClient.invalidateQueries([getProductScoresKey]);
    queryClient.invalidateQueries([getOneActionPlanKey]);
    queryClient.invalidateQueries([getOrganizationPostApiKey]);
    queryClient.invalidateQueries([getOnePostKey]);
    queryClient.invalidateQueries([getPackagingPostKey]);
    queryClient.invalidateQueries([getPackagingsPostsKey]);
    queryClient.invalidateQueries([getPostScoresKey]);
};

export const useRecalculatePost = () => {
    const queryClient = useQueryClient();
    const snackbar = useSnackbar();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: recalculatePostFn,
        onSuccess: () => invalidateRecalculatedScores(queryClient),
        onError: (error) => {
            snackbar.forceClose();
            snackbar.setSnackbarProps({
                type: "error",
                message: calculationFailureMessage(classifyCalculationFailure(error), t),
                forceAutoHideInMilisecond: 10000,
            });
        },
    });
};
